import * as React from 'react'
import { FileBox } from 'lucide-react'
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'

import EmptyCard from './EmptyCard'

import type { FeatureUtilization, ImpactResponse } from '@/api/teamInsights'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { formatAxisNumber, formatTooltipNumber } from '@/lib/utils'

const BAR_COLOR = 'var(--chart-1)'
const ROW_HEIGHT = 28
const Y_AXIS_WIDTH = 120

const FEATURE_LABELS: Record<string, string> = {
  dictionary: 'Dictionary',
  snippets: 'Snippets',
  styles: 'Styles',
  transforms: 'Transforms',
}

const SECONDARY_LABELS: Record<string, string> = {
  dictionary: 'phrases added',
  snippets: 'snippets added',
  styles: 'preferences set',
}

const KNOWN_FEATURES: { key: string; label: string }[] = [
  { key: 'dictionary', label: 'Dictionary' },
  { key: 'snippets', label: 'Snippets' },
  { key: 'styles', label: 'Styles' },
  { key: 'transforms', label: 'Transforms' },
]

const prettifyFeature = (feature: string): string =>
  FEATURE_LABELS[feature] ?? feature.charAt(0).toUpperCase() + feature.slice(1).replace(/_/g, ' ')

const formatCount = (n: number): string => formatTooltipNumber(n)

const secondaryStat = (key: string, featureUsageCount: number): string | null => {
  const label = SECONDARY_LABELS[key]
  if (!label) return null
  return `${formatCount(featureUsageCount)} ${label}`
}

interface FeatureRow {
  key: string
  label: string
  user_count: number
  secondary: string | null
}

const chartConfig = {
  user_count: { label: 'Users', color: BAR_COLOR },
} satisfies ChartConfig

interface FeaturesUsedChartProps {
  features: ImpactResponse['features_utilized']
}

const FeaturesUsedChart = ({ features }: FeaturesUsedChartProps) => {
  const byKey = React.useMemo(() => {
    const map = new Map<string, FeatureUtilization>()
    for (const f of features) map.set(f.feature, f)
    return map
  }, [features])

  const rows = React.useMemo<FeatureRow[]>(() => {
    return KNOWN_FEATURES.map((f) => {
      const fu = byKey.get(f.key)
      const userCount = fu?.user_count ?? 0
      const featureUsageCount = fu?.feature_usage_count ?? 0
      return {
        key: f.key,
        label: f.label,
        user_count: userCount,
        secondary: secondaryStat(f.key, featureUsageCount),
      }
    }).sort((a, b) => b.user_count - a.user_count)
  }, [byKey])

  const maxCount = rows.reduce((m, r) => Math.max(m, r.user_count), 0)

  if (maxCount === 0) {
    return (
      <EmptyCard
        icon={<FileBox className="size-5" />}
        title="No features used"
        description="Save shortcuts to speak the things you type all the time"
        cta={{
          label: 'Open snippets',
          onClick: () => {
            window.location.href = 'wispr-flow://open/snippets'
          },
        }}
      />
    )
  }

  return (
    <Card className="gap-0 py-0">
      <CardHeader className="flex items-center gap-4 space-y-0 border-b pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 flex-col gap-0.5">
          <CardTitle className="text-xl font-semibold">Features used</CardTitle>
          <p className="text-[15px] leading-5 text-muted-foreground">Engagement with core Flow features</p>
        </div>
      </CardHeader>
      <CardContent className="px-6 py-4">
        <ChartContainer config={chartConfig} className="w-full" style={{ height: rows.length * ROW_HEIGHT }}>
          <BarChart
            accessibilityLayer
            data={rows}
            layout="vertical"
            margin={{ top: 0, right: 96, bottom: 0, left: 4 }}
            barCategoryGap={6}
          >
            <XAxis type="number" hide domain={[0, 'dataMax']} />
            <YAxis
              type="category"
              dataKey="label"
              width={Y_AXIS_WIDTH}
              tickLine={false}
              axisLine={false}
              tick={{
                fontSize: 15,
                fontWeight: 600,
                fill: 'var(--foreground)',
                textAnchor: 'start',
                dx: -Y_AXIS_WIDTH + 4,
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  hideLabel
                  formatter={(_value, _name, item) => {
                    const row = item.payload as unknown as FeatureRow
                    return (
                      <div className="flex items-center gap-2">
                        <span className="inline-block size-3 rounded-[3px]" style={{ background: BAR_COLOR }} />
                        <span className="text-[13px] font-medium text-muted-foreground">
                          {prettifyFeature(row.key)}
                        </span>
                        {row.secondary && (
                          <span className="text-[13px] font-semibold text-foreground">{row.secondary}</span>
                        )}
                      </div>
                    )
                  }}
                />
              }
            />
            <Bar
              dataKey="user_count"
              fill={BAR_COLOR}
              radius={10}
              barSize={8}
              minPointSize={2}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="user_count"
                position="right"
                offset={8}
                width={200}
                formatter={(value) => `${formatAxisNumber(typeof value === 'number' ? value : 0)} users`}
                className="fill-foreground"
                fontSize={12}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

export default FeaturesUsedChart
